import { createSelector } from '@reduxjs/toolkit';
import React from 'react';

import { NotificationBadge } from 'src/components/NotificationBadge';
import { FlagsContext } from 'src/context';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetNotificationsSettingsQuery } from 'src/services/api/notificationsSettingsApi';
import { RootState } from 'src/store/reduxTypes';

export const MessagesNotificationBadge = () => {
  const { EnableManyCompanies } = React.useContext(FlagsContext);

  const isClientUser = useAppSelector((state) => state.user.isClient);

  const selectTotalUnreadMessagesCount = createSelector(
    [
      (state: RootState) => state.messages.totalUnreadMessages,
      (state: RootState) => state.messages.totalUnreadMessagesByCompany,
      (state: RootState) => state.activeCompany.activeCompanyId,
      (_, manyCompaniesEnabled?: boolean) => manyCompaniesEnabled,
    ],
    (
      totalUnreadMessages,
      totalUnreadMessagesByCompany,
      activeCompanyId,
      manyCompaniesEnabled,
    ) => {
      if (!manyCompaniesEnabled || !isClientUser) {
        return totalUnreadMessages || 0;
      }

      if (totalUnreadMessagesByCompany && activeCompanyId) {
        return totalUnreadMessagesByCompany[activeCompanyId] || 0;
      }

      return 0;
    },
  );

  const totalUnreadMessagesCount = useAppSelector((state) =>
    selectTotalUnreadMessagesCount(state, EnableManyCompanies),
  );

  const { data: notificationSettings } = useGetNotificationsSettingsQuery(
    undefined,
    {
      skip: isClientUser,
    },
  );
  const disabledProductNotification = notificationSettings?.disableInProduct;
  const disableMessagesNotifications =
    notificationSettings?.notifyAbout.newMessages.disableInProduct;

  const { backgroundColor, textColor } = useNotificationBadgeData(true);

  if (
    disabledProductNotification ||
    disableMessagesNotifications ||
    totalUnreadMessagesCount === 0
  )
    return null;

  return (
    <NotificationBadge
      htmlId="messages-notification-badge"
      isSidebar
      showZero={false}
      content={totalUnreadMessagesCount || 0}
      backgroundColor={backgroundColor}
      textColor={textColor}
    />
  );
};
